import type { DashEditorProps } from '.';
import type { ReadmeEditor } from '../types';
import type { $TSFixMe } from '@readme/iso';
import type { Descendant } from 'slate';

import React, { forwardRef, useCallback, useContext } from 'react';

// eslint-disable-next-line no-restricted-imports
import type { ConfigContextValue } from '@core/context';
// eslint-disable-next-line no-restricted-imports
import { ConfigContext } from '@core/context';
import useEventListener from '@core/hooks/useEventListener';
import useSentry from '@core/hooks/useSentry';

import MarkdownEditor from '@ui/MarkdownEditor';
import type Modal from '@ui/Modal';
import { NotificationToaster } from '@ui/Notification';

import ErrorModal from './ErrorModal';
import FallbackMessage from './FallbackMessage';
import HtmlMode from './HtmlMode';
import RawMode from './RawMode';
import classes from './style.module.scss';
import useLargeDocWarning from './useLargeDocWarning';

export type DashEditorNoProxyProps = Omit<DashEditorProps, 'doc' | 'setRawMode'> & {
  editorOnChange?: (value: Descendant[], editor: ReadmeEditor) => void;
  doc?: string;
  modal: Modal | null;
  onInitEditor?: (editor: ReadmeEditor) => void;
  rawOnChange?: (value: string) => void;
};

// eslint-disable-next-line react/display-name
const DashEditorNoProxy = forwardRef<HTMLDivElement, DashEditorNoProxyProps>(
  (
    {
      clickableAreaHeight = 4,
      rawMode,
      htmlMode,
      editorOnChange: _editorOnChange,
      onHtmlChange,
      onInitEditor: _onInitEditor,
      html,
      rawOnChange: _rawOnChange,
      modal,
      doc = '',
      ...props
    }: DashEditorNoProxyProps,
    ref,
  ) => {
    const sentry = useSentry('markdownEditor');
    const { checkLargeDoc } = useLargeDocWarning();
    const { asset_url: assetUrl = '' } = useContext(ConfigContext) as ConfigContextValue;

    const onInitEditor = useCallback(
      (editor: ReadmeEditor) => {
        _onInitEditor?.(editor);
        checkLargeDoc(editor);
      },
      [_onInitEditor, checkLargeDoc],
    );

    const editorOnChange = useCallback(
      (value: Descendant[], editor: ReadmeEditor) => {
        _editorOnChange?.(value, editor);
        checkLargeDoc(editor);
      },
      [_editorOnChange, checkLargeDoc],
    );

    const rawOnChange = useCallback(
      value => {
        _rawOnChange?.(value);
      },
      [_rawOnChange],
    );

    useEventListener('error', sentry, { target: (ref as React.RefObject<HTMLDivElement>)?.current });

    return (
      <div ref={ref} className={classes.DashEditor}>
        {!rawMode && !htmlMode ? (
          <FallbackMessage assetUrl={assetUrl} sentry={sentry}>
            <MarkdownEditor
              {...props}
              clickableAreaHeight={clickableAreaHeight}
              doc={doc}
              onChange={editorOnChange}
              onInit={onInitEditor}
            />
          </FallbackMessage>
        ) : (
          <>
            {/* DashEditor supports both Raw mode (pages) and HTML mode (custom pages) */}
            {htmlMode ? (
              <HtmlMode onChange={onHtmlChange} value={html} />
            ) : (
              <RawMode onBlur={props.onBlur as $TSFixMe} onChange={rawOnChange} value={doc} />
            )}
            <ErrorModal modal={modal as $TSFixMe} />
          </>
        )}
        <NotificationToaster />
      </div>
    );
  },
);

export default DashEditorNoProxy;
